import type { Locale } from './locale';

/**
 * Where the terms, the privacy policy and the refund policy live.
 *
 * They are not screens. They are static HTML written by
 * `scripts/build-legal.mjs` into `web/public/legal/` and served straight off
 * the asset store, because the two readers who matter most for them — a
 * compliance reviewer at a payment processor, and somebody who has just been
 * charged and is annoyed — must not have to boot a virtual machine to read a
 * page of text, and one of them may have scripting switched off entirely.
 *
 * So this module is the seam, and it is four lines: the app knows the address
 * and nothing else about them. A link built from it is a plain absolute path,
 * which the router in App.tsx deliberately ignores — it claims only hrefs that
 * start with `?` — so the browser navigates out of the single-page app the way
 * it would to any other site.
 *
 * ## The language, and why it is baked into the file name
 *
 * The in-app toggle repaints React and writes localStorage. Neither of those
 * exists on a page with no bundle, so a static document cannot follow it.
 * Instead there is one file per language and the link picks the right one,
 * while each page carries a link to its counterpart in that language's own
 * endonym. It is the correct trade rather than a concession: a document that
 * has to be readable with scripting off cannot have a switch made of script.
 *
 * ## The one thing that can drift
 *
 * This function and `urlFor` in the build script both spell the same URL, in
 * two languages, in two files. Nothing in the type system connects them, and a
 * mismatch would not fail a build — `not_found_handling` is
 * `single-page-application`, so a wrong path answers with the app and a 200
 * rather than a 404, which looks like a routing quirk rather than a broken
 * link. `web/test/legal.test.ts` is what actually holds the two ends together:
 * it asks this function for every address the app can produce and checks that
 * the file is there.
 */
export type LegalDoc = 'terms' | 'privacy' | 'refunds';

export const LEGAL_DOCS: readonly LegalDoc[] = ['terms', 'privacy', 'refunds'];

/**
 * The languages the documents are written in, which is not every language the
 * interface is. A legal text is not translated the way a level is: it binds
 * somebody, so a German version waits for somebody qualified to answer for it,
 * and until then a German reader is sent to the English — the same document,
 * in the language it was written in, rather than a translation nobody checked.
 * `scripts/build-legal.mjs` builds exactly these.
 */
export const LEGAL_LOCALES: readonly Locale[] = ['en', 'ar'];

export function legalHref(doc: LegalDoc, locale: Locale): string {
  const written = LEGAL_LOCALES.includes(locale) ? locale : 'en';
  // English has no infix, matching the mission sidecars and the openers: the
  // base file is the English one and every other language is a suffix on it.
  return `/legal/${doc}${written === 'en' ? '' : `.${written}`}.html`;
}
